import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KineduPalette } from 'kinedu-react-components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Content,
  Label,
  CharCount,
  PhotoUploaderContainer,
  TextInput,
  BtnWrapper,
  NextBtn,
} from './styles';
import { Button, PhotoUploader } from 'lego-dist';
import ImageFiler from 'images/ImageFiler.svg';
import themes from 'themes';

// CONFIG
import { FILES_API_URL, AWS_REGION, S3_BUCKET_FILES, AWS_KEY } from 'config';

interface IClassroom {
  id: number;
  name: string;
}

export interface AddClassroomViewProps {
  classroom?: IClassroom | undefined;
  onSubmit: (res: any) => void;
  onDelete?: () => void;
}

const AddClassroomView: React.FC<AddClassroomViewProps> = ({
  classroom,
  onSubmit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [chars, setChars] = useState(0);
  const [classroomImage]: any = useState();

  /* Validation */
  const classroomForm = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('validations:FIELD_REQUIRED'))
      .max(70, t('validations:MAX_LENGTH', { amount: 70 })),
  });

  const onUploadedAvatarToS3 = ({
    s3FilePath,
    croppedFile,
    setFieldValue,
  }: any) => {
    const s3_files_attributes = [
      {
        mime_type: croppedFile.type,
        file_name: s3FilePath,
        status: 'Complete',
        service_urls_attributes: [
          {
            record_type: 'thumbnail_url',
            url: s3FilePath,
          },
        ],
      },
    ];

    setFieldValue('avatar', { s3_files_attributes });
  };

  return (
    <Container>
      <Formik
        initialValues={{ name: classroom ? classroom.name : '', min: 1, max: '' }}
        validationSchema={classroomForm}
        onSubmit={(res: any) => onSubmit(res)}
        validateOnBlur
        validateOnChange
        initialStatus={!!classroom}
        isInitialValid={!!classroom}
        render={({ setFieldValue, errors, touched, isValid }) => (
          <Form>
            <Content>
              <p>
                {t(
                  'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.GENERAL_INFO.TITLE',
                )}
              </p>
              <PhotoUploaderContainer>
                {t(
                  'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.GENERAL_INFO.UPLOAD_IMAGE',
                )}
                <PhotoUploader
                  src={classroomImage ? classroomImage.base64 : ImageFiler}
                  previewModalProps={{headerColor: themes.colors.newKinedu }}
                  positiveButtonProps={{
                    children: t('SAVE'),
                  }}
                  negativeButtonProps={{
                    children: t('CANCEL'),
                  }}
                  onCroppedFile={({ uploadFileToS3, croppedFile }: any) => {
                    uploadFileToS3(croppedFile);
                  }}
                  onUploadedFile={({ s3FilePath, croppedFile }: any) => {
                    onUploadedAvatarToS3({
                      s3FilePath,
                      croppedFile,
                      setFieldValue,
                    });
                  }}
                  s3UploaderProps={{
                    apiURL: FILES_API_URL,
                    awsRegion: AWS_REGION,
                    bucket: S3_BUCKET_FILES,
                    aws_key: AWS_KEY,
                  }}
                  s3path={`new/avatars/groups/${Date.now()}_${
                    localStorage?.ACTIVE_CENTER_ID
                  }_`}
                  multipart
                />
              </PhotoUploaderContainer>
              <Label>
                <span>{t('settings:CLASSROOM_NAME')}</span>
                <CharCount>{chars} / 70</CharCount>
              </Label>
              <TextInput
                value={classroom ? classroom.name : ''}
                onChange={(event: any) => {
                  setChars(event.length);
                  setFieldValue('name', event);
                }}
                placeholder={t('settings:CENTER_SECTION.CLASSROOM_CONFIG.ADD_CLASSROOM_NAME')}
                maxLength={70}
                errorMessage={errors.name}
              />
            </Content>
            <BtnWrapper>
              {!classroom ? null : (
                <Button color={KineduPalette.health} onClick={onDelete}>
                  {t('settings:DELETE')}
                </Button>
              )}
              <NextBtn
                type="submit"
                color="primary"
                rounded
                flat
                disabled={!isValid}
                onSubmit={onSubmit}
              >
                {t('SAVE')}
              </NextBtn>
            </BtnWrapper>
          </Form>
        )}
      />
    </Container>
  );
};

export default AddClassroomView;
